import { useEffect, useMemo, useState, type ComponentType } from 'react'
import { Sparkles, Bug, Zap } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/shared/ui/cn'
import changelogData from '@/data/changelog.json'
import type { ChangelogEntry, ChangelogFile, ChangelogGroup } from '@/data/changelog-types'
import { markChangelogSeen } from './whats-new-seen'

const data = changelogData as ChangelogFile
const GROUP_CONFIG: Record<
  ChangelogGroup,
  { label: string; icon: ComponentType<{ className?: string }> }
> = {
  added: { label: '新增', icon: Sparkles },
  fixed: { label: '修复', icon: Bug },
  improved: { label: '优化', icon: Zap },
}

const GROUP_ORDER: ChangelogGroup[] = ['added', 'fixed', 'improved']

const CHANGELOG_TEXT_TRANSLATIONS: Record<string, string> = {
  'Motion, iris, shape, and DaVinci-style wipe transition packs':
    '运动、光圈、形状及 DaVinci 风格擦拭转场包',
  'Lens warp zoom transition': '镜头扭曲缩放转场',
  'Liquid distort transition': '液体扭曲转场',
  'Transition placement controls with alignment-aware drops': '带对齐感知的转场放置控制',
  'Searchable transition preset picker': '可搜索的转场预设选择器',
  'Tunable transition parameters in the properties panel': '属性面板中可调节的转场参数',
  'Compound clips can now host transitions': '复合片段现可承载转场效果',
  'Projects now live on disk in a workspace folder you choose':
    '项目文件现存储在您选择的工作空间文件夹中',
  'Multi-workspace support with waveform mirroring': '多工作空间支持与波形镜像同步',
  'Multi-select projects with marquee and bulk delete': '多选项目与框选及批量删除功能',
  'Trash section with soft-delete, undo, and permanent delete': '垃圾箱功能支持软删除、撤销和永久删除',
  'Legacy browser-storage migration progress banner': '本地存储迁移进度提示条',
  'Per-clip pitch shift in semitones and cents': '逐片段音高调整（半音/音分）',
  'Compact DaVinci-style six-band clip EQ with floating panel':
    '紧凑型 DaVinci 风格六段均衡器与浮动面板',
  'Mixer tuck handle to slide channel strips behind the bus':
    '混音器推拉柄可将声道条隐藏在母线后',
  'Separate project master bus from per-device monitor volume': '将项目主母线与设备监听音量分离',
  "What's New dialog shows weekly changelog": '更新说明弹窗展示每周更新日志',
  'Overhaul canvas snap system': '重构画布吸附系统',
  'Promote span text controls and move preset selector': '提升跨度文本控制并移动预设选择器',
  'Add keyframeable color effect params': '新增可关键帧控制的颜色效果参数',
  'Import media from a direct URL': '从直接 URL 导入媒体',
  'Move scenes toggle next to search as segmented control': '将场景切换移到搜索旁边并改为分段控件',
  'Dedup media imports and captions by content': '按内容对媒体导入和字幕去重',
  'Multi-segment filmstrip for compound clips': '复合片段的多片段胶片缩略图',
  'Replace card dropdown with right-click context menu': '用右键菜单替代卡片下拉菜单',
  'Wheel zoom/scroll/resize in track headers': '轨道标题栏支持滚轮缩放、滚动和调节大小',
  'Migrate to v2 layout with per-media caches': '迁移到带单媒体缓存的 v2 布局',
  'Grid view, responsive header, analyze service': '网格视图、响应式页头和分析服务',
  'Add color mode with library palette grid': '新增颜色模式和素材库调色板网格',
  'Find scenes with a similar palette': '查找色板相近的场景',
  'Add AI analysis indicators and align proxy colors': '新增 AI 分析指示并统一代理色彩',
  'Add AI caption generation with timeline insertion': '新增 AI 字幕生成和插入时间线能力',
  'Expand glyph atlas text rendering': '扩展字形图集文本渲染',
  'Enable GPU transition compositing': '启用 GPU 转场合成',
  'Effect drag overlays no longer stick or hijack adjacent lanes':
    '效果拖拽覆盖层不再粘滞或劫持相邻轨道',
  'Transition preset selection syncs with the chosen direction': '转场预设选择会与所选方向同步',
  'Removed the transition duration cap': '移除转场时长上限',
  'Removed Kokoro voice quality options': '移除 Kokoro 语音质量选项',
  'Preview no longer fails when media blobs expire': '媒体 Blob 过期时预览不再失败',
  'Disabled tracks remain editable and styled after reload': '禁用轨道重载后仍保持可编辑和原有样式',
  'Pen mask tracks are now visible in classic timelines': '经典时间线中现可显示钢笔遮罩轨道',
  'Transitions stay aligned to source time across splits': '转场在片段分割后仍保持源时间对齐',
  'Pen paths default to shapes with 5-second duration': '钢笔路径默认生成 5 秒时长的形状',
  'UI accessibility, transition alpha, and viewport clamp fixes':
    '修复 UI 无障碍、转场透明度和视口边界问题',
  'Resolve develop type errors': '修复开发阶段的类型错误',
  'Respect visible params and safer effect mapping': '遵循可见参数并采用更安全的效果映射',
  'Allow animating non-color GPU effect params': '允许为非颜色 GPU 效果参数添加动画',
  'Route effects keyframe deps through adapters': '通过适配器路由效果关键帧依赖',
  'Remove unused crop helpers': '移除未使用的裁剪辅助函数',
  'Smooth live pitch preview on Semi Tones / Cents sliders': '半音和音分滑块支持平滑实时音高预览',
  'Pixel-snap audio volume line to avoid subpixel blur': '让音频音量线像素对齐，避免亚像素模糊',
  'Render compound clip hover preview via canvas engine': '通过画布引擎渲染复合片段悬停预览',
  'Scale compound clip hover preview to fit wrapper': '缩放复合片段悬停预览以适配容器',
  'Stop nested video black flashes from stale sequenceFromRef':
    '修复旧 sequenceFromRef 导致的嵌套视频黑闪',
  'Restore MMB drag to resize A/V divider from anywhere': '恢复中键拖拽，支持从任意位置调整音视频分隔线',
  'Version shared caption cache by sample interval': '按采样间隔为共享字幕缓存做版本隔离',
  'Stop blanking project list with spinner during mutations': '操作过程中不再用加载动画清空项目列表',
  'Eliminate Add Effect picker flash on first open': '消除添加效果选择器首次打开的闪烁',
  'Constrain new project page width to 7xl': '将新建项目页宽度限制为 7xl',
  'Refresh nested compound clip effects during playback': '播放期间刷新嵌套复合片段效果',
  'Route SubComposition type through deps adapter': '通过依赖适配器路由 SubComposition 类型',
  'Use live sub-comp duration for compound clip trim and display': '使用实时子合成时长驱动复合片段修剪和显示',
  'Correct available pane height when no track sections': '无轨道分区时修正可用面板高度',
  'Reliably repaint compound clips on sub-comp edits and nav': '在子合成编辑和跳转时可靠重绘复合片段',
  'Invalidate frame cache when sub-compositions change': '子合成变更时使帧缓存失效',
  'Apply gpu effects and adjustment layers inside compound clips': '在复合片段内部应用 GPU 效果和调整图层',
  'Input handling edge cases': '修复输入处理边界情况',
  'Prevent header toolbar from wrapping on narrow widths': '防止标题工具栏在窄宽度下换行',
  'Add grid-cols-1 to DialogContent': '为 DialogContent 添加 grid-cols-1',
  'Hydrate proxies from workspace on fresh origins': '在新环境下从工作空间恢复代理数据',
  'Remove motion semantic search, tighten color queries': '移除运动语义搜索并收紧颜色查询',
  'Use palette-only ranking for color queries': '颜色查询仅按调色板排序',
  'Route timeline transcribe dialog through media contract': '通过媒体契约路由时间线转录弹窗',
  'Move preview and filmstrip caches into workspace fs': '将预览和胶片缓存迁移到工作空间文件系统',
  'Queue and unify transcription progress flows': '排队并统一转录进度流程',
  'Transcribe custom-decoded audio from conformed wav': '从标准化 WAV 中转录自定义解码音频',
  'Anchor timeline slider zoom at the playhead': '让时间线滑块缩放锚定在播放头位置',
  'Route analysis imports through infrastructure facade': '通过基础设施门面路由分析导入',
  'Route preview scrub throttle through contract': '通过契约层路由预览擦动节流',
  'Keep AI analysis off timeline caption tracks': '避免将 AI 分析写入时间线字幕轨道',
  'Lower skim overlay below popovers': '将扫读覆盖层置于浮层下方',
  'Unify ruler scrub handoff to avoid preview flashes': '统一标尺擦动切换，避免预览闪烁',
  'Speed up media skimming and preserve program preview': '加快媒体扫读并保留节目预览',
  'Sync preview timecode and playhead during scrubbing': '擦动时同步预览时间码和播放头',
  'Prefer Player path for generated caption scrubs': '生成字幕擦动时优先走 Player 路径',
  'Harden text atlas, transition cache, and compositor buffer pool':
    '增强文本图集、转场缓存和合成器缓冲池稳定性',
  'Update commit message generation': '更新提交信息生成逻辑',
  'Preserve mask coverage in GPU blend transitions': '在 GPU 混合转场中保留遮罩覆盖范围',
  'Preserve GPU layer uniforms per pass': '按渲染通道保留 GPU 图层 uniform',
  'Skip occluded sub-comp tracks in export rendering': '导出渲染时跳过被遮挡的子合成轨道',
  'Handle commit message generation request': '处理提交信息生成请求',
  'Flatten curved GPU shape paths': '将曲线 GPU 形状路径扁平化',
  'Generate single-line commit message': '生成单行提交信息',
  'Keep GPU shape effects on texture path': '保持 GPU 形状效果走纹理路径',
  'Share transition participant rendering': '共享转场参与方渲染结果',
  'Route audio time stretch through infrastructure': '通过基础设施路由音频时间拉伸',
  'Keep generated reports parseable in checks': '保证生成报告在检查流程中可解析',
  'Keep runtime hooks in sync with latest refs': '保持运行时 hooks 与最新 refs 同步',
  'Tighten memo deps in preview and timeline hooks': '收紧预览和时间线 hooks 的 memo 依赖',
  'Compact clip shell for narrow clips with short-circuited fades': '窄片段使用更紧凑的外壳和短路淡变',
  'Smoother filmstrip rendering when zooming the timeline': '缩放时间线时胶片缩略图渲染更流畅',
  'Smaller 960×540 proxy resolution with worker-side loading': '采用更小的 960×540 代理分辨率和工作线程加载',
  'Kick re-render as soon as priority media is ready': '优先媒体就绪后立即触发重渲染',
  'Subscription-based controller isolates overlay re-renders': '基于订阅的控制器隔离覆盖层重渲染',
  'Clip EQ with five-band presets across preview and export': '预览和导出均支持五段预设片段均衡器',
  'SoundTouch-based preview audio replaces WAV path': '基于 SoundTouch 的预览音频替代 WAV 路径',
  'Alt+C as alternate split-at-playhead shortcut': '新增 Alt+C 作为播放头分割备用快捷键',
  'AI-powered media captioning with LFM vision model': '使用 LFM 视觉模型的 AI 媒体字幕能力',
  'Local MusicGen music generation with presets and cancellation': '本地 MusicGen 音乐生成，支持预设和取消',
  'Gemma-4 scene cut verification and LFM 2.5 VL alternative': 'Gemma-4 场景切割验证和 LFM 2.5 VL 替代方案',
  'Fast histogram-based scene detection': '快速直方图场景检测',
  'Local model cache management in settings': '在设置中管理本地模型缓存',
  'Source monitor patch destination pickers': '源监视器补丁目标选择器',
  'Delete shortcut routes to keyframe editor when active': '关键帧编辑器激活时，删除快捷键自动路由到该面板',
  'Split linked items together without sync drift': '链接项目可一起分割且不会产生同步漂移',
  'Glitch transition shader no longer paints black regions': '故障转场着色器不再渲染黑块区域',
  'Context menu submenu clicks no longer deselect items': '右键菜单子菜单点击不再取消当前选择',
  'Same-origin transition exit no longer pops a frame': '同源转场退出时不再多跳一帧',
  'Vorbis audio and nested media play through proxies': 'Vorbis 音频和嵌套媒体现可通过代理播放',
  'Stale timeline in/out points are clamped': '过期的时间线入点和出点现会自动收敛',
  'Filmstrip rewritten: no edge gap, pop, or zoom-driven re-renders':
    '重写胶片缩略图：消除边缘空隙、跳变和缩放触发的重渲染',
  'Reduced drag, marquee, and scroll-driven re-renders': '减少拖拽、框选和滚动触发的重渲染',
  'Editor dialogs now lazy-load': '编辑器弹窗现改为懒加载',
  'Frame-accurate source time snapping prevents skipped frames': '基于帧精度的源时间吸附，避免跳帧',
  'Nested compound clips with cycle detection and deep-nested rename/delete':
    '嵌套复合片段支持循环检测及深层重命名/删除',
  'Crop-aware media layout with soft-edge feathering': '支持裁剪感知布局和软边羽化',
  'SVG import, image filmstrips, and draggable text/shape templates': '支持 SVG 导入、图片胶片缩略图和可拖拽文本/形状模板',
  'Renamed Composition to Compound Clip across UI': '全站 UI 中将 Composition 更名为复合片段',
  'AI Text-to-Speech panel with local WebGPU inference': 'AI 文本转语音面板，支持本地 WebGPU 推理',
  'AV1 codec support in export with runtime capability checks': '导出支持 AV1 编码，并带运行时能力检测',
  'Media library hover-skim preview in program monitor': '节目监视器支持媒体库悬停扫读预览',
  'Audio preview play button on media cards': '媒体卡片新增音频预览播放按钮',
  'Mixer fader and mute on the bus master channel': '母线主通道支持混音推子和静音',
  'Edge halo system for trim handles with constraint feedback': '修剪手柄新增边缘光晕系统和约束反馈',
  'Track push/pull handle for multi-track gap adjustment': '新增轨道推拉手柄，用于多轨空隙调整',
  'Full-column toggle for properties and media sidebars': '属性栏和媒体栏支持整列切换',
  'Per-card info popover replaces bottom info panel': '每张卡片的信息浮层替代底部信息面板',
  'Video fade in/out handles applied in preview and export': '视频淡入淡出手柄同时应用于预览和导出',
  'Single disable toggle unifies track visibility and mute': '单个禁用开关统一轨道可见性和静音',
  'Default FPS, snap, preview quality, and export defaults in settings': '设置中支持默认帧率、吸附、预览质量和导出默认值',
  'Smart ripple behavior on rate-stretch tool': '速率拉伸工具新增智能波纹行为',
  'Auto-match project canvas and FPS to first dropped video': '自动匹配项目画布和帧率到首个拖入视频',
  'Alt+scroll to resize tracks from headers': '支持 Alt+滚轮在标题栏中调整轨道高度',
  'GPU transition effect renders when paused on a transition frame': '暂停在转场帧时也能正确渲染 GPU 转场',
  'Sub-pixel seam gone from uncropped edges': '移除未裁剪边缘的亚像素接缝',
  'Masks apply in composition space with stable refs': '遮罩会在合成空间中以稳定引用生效',
  'Clip volume edits persist with stereo waveform channels': '片段音量编辑可与立体声波形声道一同持久化',
  'Per-track meter preview during fader drag': '拖动推子时支持逐轨电平预览',
  'Slide and linked-clip clamping respects transitions and limits': '滑动和链接片段的约束会正确考虑转场与边界',
  'Duplicate slide limit boxes on neighbor counterparts removed': '移除邻接对象上的重复滑动限制框',
  'Overlapping items on the same track now detected and prevented': '现已检测并阻止同轨道项目重叠',
  'Trim, ripple, rolling, slip, and slide tools with live previews': '修剪、波纹、滚动、滑移和滑动工具支持实时预览',
  'Smart zone detection and constraint feedback while editing': '编辑时支持智能区域检测和约束反馈',
  'Tool operation overlay with compact limit edges': '新增带紧凑限制边缘的工具操作覆盖层',
  'Linked audio-video items replace track groups': '链接的音视频项目替代轨道组方案',
  'Compound wrappers: speed, transitions, trim, slip/slide, linked audio':
    '复合包装支持速度、转场、修剪、滑移/滑动和链接音频',
  'Split and join compound wrappers with crossfade playback': '支持分割和合并复合包装，并带交叉淡化播放',
  'Cut-centered handle-based transitions replace overlap model': '以切口为中心的手柄式转场替代旧重叠模型',
  'Transition drag tooltip and drop ghost polish': '优化转场拖拽提示和放置幽灵效果',
  'Audio fade curves with power-curve model and edge snapping': '音频淡变曲线支持幂曲线模型和边缘吸附',
  'Media drop zones and track drop preview': '新增媒体投放区域和轨道投放预览',
  'Content-based track drag lane moves': '支持基于内容的轨道拖拽换道',
  'Track-kind-aware item placement': '项目放置支持轨道类型感知',
  'Drag-and-drop effects onto clips and draggable adjustment layers':
    '支持将效果拖放到片段上，并可拖拽调整图层',
  'Floating dockable mixer with stereo LED meters and track colors': '新增可浮动停靠的混音器，支持立体声 LED 表头和轨道配色',
  'Real-time volume and meter levels during fader drag': '拖动推子时实时显示音量和电平',
  'Dopesheet with property accordion groups and marquee selection': 'Dopesheet 支持属性手风琴分组和框选',
  'Bezier tangent mirroring, multi-curve overlay, and compact navigator':
    '支持贝塞尔切线镜像、多曲线叠加和紧凑导航器',
  'Full-height media sidebar with in-panel keyframe editor': '全高媒体侧边栏内置关键帧编辑器',
  'Transition selector dropdown replaces large grid': '转场选择器下拉菜单替代大网格',
  'Compact source and program monitor toolbars': '源监视器和节目监视器工具栏改为紧凑样式',
  'Source patch controls moved into the source monitor': '源补丁控件已移入源监视器',
  'Track resize now feels anchored, Resolve-style': '轨道缩放手感更稳定，接近 Resolve 风格',
  'Transition audio doubling eliminated': '修复转场音频重复叠加问题',
  'Prewarm worker initialization and transition cold decode stalls': '预热工作线程初始化并减少转场冷启动解码卡顿',
  'Mixer fader decoupled from store writes for smooth dragging': '混音器推子与 store 写入解耦，拖动更顺滑',
  'Adaptive seek backtracking via keyframe index': '通过关键帧索引实现自适应回溯寻帧',
  'Batch preseek and faster keyframe extraction': '支持批量预寻帧和更快的关键帧提取',
  'Eager WASM warmup and batch prearm for transitions': '转场支持主动 WASM 预热和批量预备',
  'Narrow render-critical store subscriptions': '收窄渲染关键路径的 store 订阅范围',
  'Pen mode with canvas drop, scopes, and interaction lock': '钢笔模式支持画布投放、作用域和交互锁定',
  'Mask editing with in-panel keyframe editor': '遮罩编辑支持面板内关键帧编辑器',
  'Audio transcription': '音频转录',
  'Explicit auto-keyframe arming via dopesheet toggle': '通过 Dopesheet 开关显式启用自动关键帧',
  'GPU shader migration for all transitions with export parity': '全部转场迁移到 GPU 着色器并保证导出一致性',
  'Transition playback jitter and scrub stability': '修复转场播放抖动并提升擦动稳定性',
  'Waveform now renders on initial media drop': '首次拖入媒体时即可渲染波形',
  'Properties sidebar scrollbar': '修复属性侧边栏滚动条',
  'Export blits GPU composite directly to canvas (no readback)': '导出时将 GPU 合成结果直接写入画布，无需回读',
  'Cold-start playback stalls eliminated': '消除冷启动播放卡顿',
  'Timeline scroll and zoom rendering optimizations': '优化时间线滚动和缩放渲染性能',
  'Distort and stylize effect family with color parameters': '新增支持颜色参数的扭曲和风格化效果族',
  'Structured wide-event logging across core features': '在核心功能中加入结构化宽事件日志',
  'No more infinite retry storms on failed video source init': '视频源初始化失败时不再出现无限重试风暴',
  'Audio clicks and spurious video warnings eliminated during playback': '修复播放期间的音频爆音和误报视频警告',
  'Keyframed transforms apply on ruler frame seeks': '标尺寻帧时也会正确应用关键帧变换',
  'Stale fast-scrub renders no longer flash after scrub exit': '快速擦动退出后不再闪现过期渲染结果',
  'Gizmo and keyframe scrub stay in sync with the preview frame': 'Gizmo 和关键帧擦动与预览帧保持同步',
  'Hidden adjustment tracks now respected in canvas renderer': '画布渲染器现会正确处理隐藏的调整轨道',
  'Indexed stores, streaming proxies, and cost-aware resolution': '引入索引化存储、流式代理和成本感知分辨率策略',
  'Memory-aware filmstrip cache with batch optimizations': '胶片缓存支持内存感知和批量优化',
  'Export: packet remux fast path and streaming source': '导出支持数据包重封装快速路径和流式源',
  'Strict-decode video frames in edit 2-up overlay with legacy fallback':
    '在编辑双画面对比覆盖层中严格解码视频帧，并保留旧版回退',
  'Chunked window optimizations for export, timeline, and waveforms': '导出、时间线和波形支持分块窗口优化',
  'Ripple edit tool with downstream clip shifting': '新增波纹编辑工具，可推动后续片段',
  'Rolling edit with 2-up frame comparison overlay': '新增滚动编辑，支持双画面对比覆盖层',
  'Slip and slide editing with live filmstrip preview': '滑移和滑动编辑支持实时胶片缩略图预览',
  'Font picker with searchable catalog and live preview': '字体选择器支持可搜索目录和实时预览',
  'Proxy playback toggle and proxy management UI': '新增代理播放开关和代理管理界面',
  'Option to delete local files when deleting a project': '删除项目时可选择同时删除本地文件',
  'Single-page new project with inline template picker': '单页新建项目流程，内联模板选择器',
  'Undo history depth setting': '新增撤销历史深度设置',
  'AC-3/E-AC-3 codec support': '支持 AC-3/E-AC-3 编码',
  'Streaming partial audio decode with wavesurfer-style rendering': '支持流式局部音频解码和 wavesurfer 风格渲染',
  'Sub-composition audio extraction in export': '导出时支持子合成音频提取',
  'Animated WebP support and transparent GIF rendering': '支持动画 WebP 和透明 GIF 渲染',
  'Per-track Close Gaps button replaces magnetic mode': '逐轨关闭间隙按钮替代磁性模式',
  '3-row clip layout with dedicated label row': '三行片段布局，带独立标签行',
  'Project-scoped cache, thumbnail, and proxy controls': '新增项目级缓存、缩略图和代理控制',
  'NLE-style continuous filled-path waveform rendering': 'NLE 风格连续填充路径波形渲染',
  'FCP-style transition bridge with live ripple preview': 'FCP 风格转场桥接，支持实时波纹预览',
  'Shift+Z to zoom to 100% at cursor or playhead': 'Shift+Z 可在光标或播放头位置缩放到 100%',
  'Interactive razor mode with tool-aware playhead colors': '交互式剃刀模式支持工具感知的播放头颜色',
  'Brave browser support guidance in media library': '媒体库新增 Brave 浏览器支持提示',
  'Stale properties panel on clip selection': '修复选择片段后属性面板显示过期内容的问题',
  'Audio stable across split boundaries during playback': '播放时音频跨分割边界保持稳定',
  'Playback and proxy seek no longer hang': '播放和代理寻帧不再卡死',
  'Clock frozen while tab is hidden prevents reseek stutter': '标签页隐藏时冻结时钟，避免重新寻帧卡顿',
  'Recovery from stale blob URLs after tab inactivity': '标签页长时间不活动后可从过期 Blob URL 中恢复',
  '1px gaps from clip edge rounding eliminated': '消除片段边缘圆整导致的 1px 缝隙',
  'Block split and razor cuts inside transition overlap zones': '禁止在转场重叠区域内进行分割和剃刀切割',
  'Stale asset errors now show a save prompt': '过期素材报错现在会提示保存项目',
  'Video source pooling reduces transition flicker': '视频源池化减少转场闪烁',
  'Track groups with collapse, drag, and gate behavior': '新增轨道组，支持折叠、拖拽和门控行为',
  'Source monitor with In/Out points and Insert/Overwrite editing': '源监视器支持入点/出点和插入/覆盖编辑',
  'Animatable volume and gain via keyframes': '音量和增益支持关键帧动画',
  'Pre-compositions: group clips into nested sequences': '预合成能力：可将片段分组为嵌套序列',
  'Reverse clip, freeze frame, and magnetic timeline mode': '支持反向片段、冻结帧和磁性时间线模式',
  'Bento layout presets with interactive drag-to-swap canvas': '新增 Bento 布局预设和交互式拖拽交换画布',
  'Inline preview player in the export complete view': '导出完成视图内置预览播放器',
  'On-demand 720p proxy video generation': '支持按需生成 720p 代理视频',
  'Drag-to-resize sidebars with persisted widths': '侧边栏支持拖拽调宽并记忆宽度',
  'Source monitor with split layout and slide-up media info': '源监视器支持分栏布局和上滑媒体信息',
  'Hover-based keyboard shortcuts for source monitor': '源监视器支持悬停式快捷键提示',
  'Transition system overhaul: split-clip stutter and flip flash gone': '重构转场系统，消除分割卡顿和翻转闪烁',
  'Export corner radius and audio quality now match preview': '导出的圆角半径和音频质量现与预览一致',
  'Correct source-FPS conversion for clip trim points': '修复片段修剪点的源帧率换算',
  'Toggling track visibility no longer leaves stale opacity': '切换轨道可见性不再残留错误透明度',
  'Segments and transitions can move on hidden tracks': '片段和转场现可在隐藏轨道上移动',
  'Prevent dropping items onto group tracks': '禁止将项目投放到轨道组上',
  'Export shortcut (Ctrl+E) overrides Chrome default': '导出快捷键 Ctrl+E 可覆盖 Chrome 默认行为',
  'App-wide error boundaries and toast notifications': '全应用错误边界和 Toast 通知',
  'Frame callback drift correction and smoother resume playback': '修正帧回调漂移，让恢复播放更顺滑',
  'Filmstrip extraction throttled for lower CPU load': '胶片缩略图提取支持节流，降低 CPU 占用',
  'Multi-track timeline: drag, drop, trim, razor, zoom, snap': '多轨时间线：拖拽、投放、修剪、剃刀、缩放、吸附',
  'Media library with import, metadata, thumbnails, and waveforms': '媒体库支持导入、元数据、缩略图和波形',
  'Live preview with resizable layout': '实时预览支持可调布局',
  'Transition library with 24+ effects': '转场库内置 24+ 种效果',
  'Keyframe animation with Bezier easing and graph editor': '关键帧动画支持贝塞尔缓动和曲线编辑器',
  'GPU effects and blend modes': 'GPU 效果和混合模式',
  'Video export with MP3 audio and animated GIF support': '视频导出支持 MP3 音频和动画 GIF',
  'Project save/load with schema versioning and migrations': '项目保存/加载支持结构版本和迁移',
  'Project bundle (ZIP) export and import': '项目包（ZIP）导出和导入',
  'Customizable keyboard shortcuts': '可自定义快捷键',
  'In/out points for selective timeline exports': '入点/出点支持选择性时间线导出',
  'Hover playhead preview with video seeking': '悬停播放头预览支持视频寻帧',
  'Initial release': '初始版本',
}

function translateChangelogText(text: string): string {
  return CHANGELOG_TEXT_TRANSLATIONS[text] ?? text
}

function formatEntryLabel(entry: ChangelogEntry): string {
  return entry.version === 'current' ? '本周' : entry.version
}

function formatWeekRange(mondayIso: string): string {
  const monday = new Date(`${mondayIso}T00:00:00Z`)
  const sunday = new Date(monday.getTime() + 6 * 24 * 60 * 60 * 1000)
  const fmt = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  })
  return `${fmt.format(monday)} — ${fmt.format(sunday)}`
}

function formatEntrySubtitle(entry: ChangelogEntry): string {
  if (entry.subtitle) return translateChangelogText(entry.subtitle)
  if (entry.version === 'current') return `截至 ${formatSingleDate(entry.date)}`
  return `当周：${formatWeekRange(entry.date)}`
}

function formatSingleDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`)
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(d)
}

interface WhatsNewDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function WhatsNewDialog({ open, onOpenChange }: WhatsNewDialogProps) {
  const entries = useMemo<ChangelogEntry[]>(
    () => (data.current ? [data.current, ...data.releases] : data.releases),
    [],
  )

  const [selectedVersion, setSelectedVersion] = useState<string>(() => entries[0]?.version ?? '')

  useEffect(() => {
    if (open) markChangelogSeen()
  }, [open])

  const selected = entries.find((e) => e.version === selectedVersion) ?? entries[0]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl h-[72vh] p-0 flex flex-col overflow-hidden gap-0">
        <DialogHeader className="px-6 pt-5 pb-3">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            更新说明
          </DialogTitle>
        </DialogHeader>
        <Separator />
        <div className="flex flex-1 overflow-hidden min-h-0">
          <nav className="w-56 border-r shrink-0 bg-muted/20">
            <ScrollArea className="h-full">
              <ul className="p-2 space-y-0.5">
                {entries.map((entry) => {
                  const isCurrent = entry.version === 'current'
                  const isSelected = selectedVersion === entry.version
                  return (
                    <li key={entry.version}>
                      <button
                        type="button"
                        onClick={() => setSelectedVersion(entry.version)}
                        className={cn(
                          'w-full text-left px-3 py-2 rounded-md text-sm transition-colors',
                          isSelected ? 'bg-accent' : 'hover:bg-accent/50',
                        )}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium truncate">{formatEntryLabel(entry)}</span>
                          {isCurrent && (
                            <span className="text-[10px] uppercase tracking-wide text-primary bg-primary/10 px-1.5 py-0.5 rounded shrink-0">
                              新
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5 truncate">
                          {formatEntrySubtitle(entry)}
                        </div>
                      </button>
                    </li>
                  )
                })}
              </ul>
            </ScrollArea>
          </nav>
          <ScrollArea className="flex-1 min-w-0">
            {selected && <ChangelogEntryView entry={selected} />}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function ChangelogEntryView({ entry }: { entry: ChangelogEntry }) {
  return (
    <div className="px-6 py-5 space-y-6">
      {GROUP_ORDER.map((groupKey) => {
        const items = entry.groups[groupKey]
        if (!items || items.length === 0) return null
        const { label, icon: Icon } = GROUP_CONFIG[groupKey]
        return (
          <section key={groupKey} className="space-y-2.5">
            <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Icon className="h-3.5 w-3.5" />
              {label}
            </h3>
            <ul className="space-y-1.5 list-disc pl-5 marker:text-muted-foreground/60">
              {items.map((item, i) => (
                <li key={i} className="text-xs leading-relaxed">
                  {translateChangelogText(item.title)}
                </li>
              ))}
            </ul>
          </section>
        )
      })}
    </div>
  )
}
